import { post, BadRequestError } from '../lib'

type UpgradeAuditNamedEntityRef = {
  id: number,
  name: string
}

type UpgradeAuditDeviceRef = UpgradeAuditNamedEntityRef & {
  serial: string
}

type UpgradeAuditVehicleRef = UpgradeAuditNamedEntityRef & {
  vin: string | null
}

type UpgradeAuditVersionValue = {
  ver: string,
  lastUpdated: string
}

type UpgradeAuditDownloadRef = {
  id: number,
  dateCreated: string,
  received: string | null
}

type UpgradeAuditDownloadFileRef = {
  id: number,
  name: string
}

type UpgradeAuditScriptIdRef = {
  id: number
}

type UpgradeAuditScriptRef = UpgradeAuditNamedEntityRef | null

type UpgradeAuditPendingScriptFileSendJob = {
  type: 'SCRIPT',
  id: number,
  runStatus: 'NEW' | 'STARTED',
  download: UpgradeAuditDownloadRef,
  downloadFile: UpgradeAuditDownloadFileRef,
  script: UpgradeAuditScriptIdRef
}

type UpgradeAuditPendingSoftwareUpdateFileSendJob = {
  type: 'SOFTWARE_UPDATE',
  id: number,
  runStatus: 'NEW' | 'STARTED',
  download: UpgradeAuditDownloadRef,
  downloadFile: UpgradeAuditDownloadFileRef
}

export type SoftwareConformanceStatus = 'match' | 'mismatch' | 'unconfigured'

export type SoftwareConformanceComponentName = 'os' | 'client' | 'zchip' | 'schip'

export type SoftwareConformanceComponent = {
  name: SoftwareConformanceComponentName,
  actual: string | null,
  expected: string,
  match: boolean
}

export type ConfiguredSoftwareUpdateSource = 'VEHICLE' | 'FLEET' | 'ORGANIZATION'

export type ConfiguredSoftwareUpdate = {
  source: ConfiguredSoftwareUpdateSource,
  deviceUpdate: UpgradeAuditNamedEntityRef,
  downloadFile: UpgradeAuditDownloadFileRef | null
}

export type CoreminiChange = {
  actual: string | null,
  expected: string | null,
  willChange: boolean
}

export const NO_COREMINI_CHANGE: CoreminiChange = {
  actual: null,
  expected: null,
  willChange: false
}

export type DeviceUpdateAuditItem = {
  device: UpgradeAuditDeviceRef,
  vehicle: UpgradeAuditVehicleRef,
  fleet: UpgradeAuditNamedEntityRef,
  organization: UpgradeAuditNamedEntityRef,
  versions: {
    os: UpgradeAuditVersionValue | null,
    client: UpgradeAuditVersionValue | null,
    zchip: UpgradeAuditVersionValue | null,
    schip: UpgradeAuditVersionValue | null
  },
  script: {
    current: UpgradeAuditScriptRef,
    lastSent: {
      download: UpgradeAuditDownloadRef,
      downloadFile: UpgradeAuditDownloadFileRef,
      script: UpgradeAuditScriptIdRef,
      matchesCurrent: boolean
    } | null
  },
  softwareUpdate: {
    lastSent: {
      download: UpgradeAuditDownloadRef,
      downloadFile: UpgradeAuditDownloadFileRef
    } | null
  },
  configuredSoftwareUpdate: ConfiguredSoftwareUpdate | null,
  softwareConformance: {
    status: SoftwareConformanceStatus,
    components: SoftwareConformanceComponent[]
  },
  coreminiChange?: CoreminiChange,
  pendingFileSendJobs: Array<UpgradeAuditPendingScriptFileSendJob | UpgradeAuditPendingSoftwareUpdateFileSendJob>
}

export type DeviceSoftwareRemediationResult = {
  device: UpgradeAuditNamedEntityRef,
  deviceUpdate: UpgradeAuditNamedEntityRef,
  download: UpgradeAuditDownloadRef,
  downloadFile: UpgradeAuditDownloadFileRef
}

export type DeviceSoftwareBulkRemediationFailure = {
  deviceId: number,
  deviceName?: string | null,
  error: string
}

export type DeviceSoftwareBulkRemediationResult = {
  succeeded: DeviceSoftwareRemediationResult[],
  failed: DeviceSoftwareBulkRemediationFailure[]
}

type DeviceUpdatesRequest = {
  organizationIds: number[],
  fleetIds: number[]
}

export type GetDeviceUpdateAuditParams = {
  organizations: number[],
  fleets: number[],
  refreshNonce?: number
}

const SOFTWARE_CONFORMANCE_STATUSES: ReadonlySet<string> = new Set([
  'match',
  'mismatch',
  'unconfigured'
])

const SOFTWARE_CONFORMANCE_NAMES: ReadonlySet<string> = new Set([
  'os',
  'client',
  'zchip',
  'schip'
])

const CONFIGURED_SOURCES: ReadonlySet<string> = new Set([
  'VEHICLE',
  'FLEET',
  'ORGANIZATION'
])

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null
)

const isNamedEntityRef = (value: unknown): value is UpgradeAuditNamedEntityRef => (
  isRecord(value)
    && typeof value.id === 'number'
    && typeof value.name === 'string'
)

const isDownloadFileRef = (value: unknown): value is UpgradeAuditDownloadFileRef => (
  isNamedEntityRef(value)
)

const isDownloadRef = (value: unknown): value is UpgradeAuditDownloadRef => (
  isRecord(value)
    && typeof value.id === 'number'
    && typeof value.dateCreated === 'string'
    && (value.received === null || typeof value.received === 'string')
)

const isSoftwareConformanceComponent = (value: unknown): value is SoftwareConformanceComponent => (
  isRecord(value)
    && typeof value.name === 'string'
    && SOFTWARE_CONFORMANCE_NAMES.has(value.name)
    && (value.actual === null || typeof value.actual === 'string')
    && typeof value.expected === 'string'
    && typeof value.match === 'boolean'
)

const isConfiguredSoftwareUpdate = (value: unknown): value is ConfiguredSoftwareUpdate => (
  isRecord(value)
    && typeof value.source === 'string'
    && CONFIGURED_SOURCES.has(value.source)
    && isNamedEntityRef(value.deviceUpdate)
    && (value.downloadFile === null || isDownloadFileRef(value.downloadFile))
)

const isCoreminiChange = (value: unknown): value is CoreminiChange => (
  isRecord(value)
    && (value.actual === null || typeof value.actual === 'string')
    && (value.expected === null || typeof value.expected === 'string')
    && typeof value.willChange === 'boolean'
)

const isDeviceUpdateAuditItem = (value: unknown): value is DeviceUpdateAuditItem => {
  if (!isRecord(value)) {
    return false
  }

  if (!(
    isRecord(value.device)
    && isRecord(value.vehicle)
    && isRecord(value.fleet)
    && isRecord(value.organization)
    && isRecord(value.versions)
    && isRecord(value.script)
    && isRecord(value.softwareUpdate)
    && Array.isArray(value.pendingFileSendJobs)
    && isRecord(value.softwareConformance)
  )) {
    return false
  }

  const conformance = value.softwareConformance
  if (
    typeof conformance.status !== 'string'
    || !SOFTWARE_CONFORMANCE_STATUSES.has(conformance.status)
    || !Array.isArray(conformance.components)
    || !conformance.components.every(isSoftwareConformanceComponent)
  ) {
    return false
  }

  if (value.coreminiChange !== undefined && !isCoreminiChange(value.coreminiChange)) {
    return false
  }

  return value.configuredSoftwareUpdate === null
    || isConfiguredSoftwareUpdate(value.configuredSoftwareUpdate)
}

const isDeviceSoftwareRemediationResult = (
  value: unknown
): value is DeviceSoftwareRemediationResult => (
  isRecord(value)
    && isNamedEntityRef(value.device)
    && isNamedEntityRef(value.deviceUpdate)
    && isDownloadRef(value.download)
    && isDownloadFileRef(value.downloadFile)
)

const isDeviceSoftwareBulkRemediationFailure = (
  value: unknown
): value is DeviceSoftwareBulkRemediationFailure => (
  isRecord(value)
    && typeof value.deviceId === 'number'
    && (value.deviceName === undefined || value.deviceName === null || typeof value.deviceName === 'string')
    && typeof value.error === 'string'
)

const isDeviceSoftwareBulkRemediationResult = (
  value: unknown
): value is DeviceSoftwareBulkRemediationResult => (
  isRecord(value)
    && Array.isArray(value.succeeded)
    && value.succeeded.every(isDeviceSoftwareRemediationResult)
    && Array.isArray(value.failed)
    && value.failed.every(isDeviceSoftwareBulkRemediationFailure)
)

export const formatDeviceUpdateAuditResponse = (res: unknown): DeviceUpdateAuditItem[] => {
  if (!Array.isArray(res) || !res.every(isDeviceUpdateAuditItem)) {
    throw new Error('Invalid device update audit response')
  }

  return res.map((item) => ({
    ...item,
    coreminiChange: item.coreminiChange ?? NO_COREMINI_CHANGE
  }))
}

export const formatDeviceSoftwareRemediationResponse = (
  res: unknown
): DeviceSoftwareRemediationResult => {
  if (!isDeviceSoftwareRemediationResult(res)) {
    throw new Error('Invalid device software remediation response')
  }

  return res
}

export const formatDeviceSoftwareBulkRemediationResponse = (
  res: unknown
): DeviceSoftwareBulkRemediationResult => {
  if (!isDeviceSoftwareBulkRemediationResult(res)) {
    throw new Error('Invalid device software bulk remediation response')
  }

  return res
}

export const getDeviceUpdateAudit = async ({
  organizations,
  fleets
}: GetDeviceUpdateAuditParams): Promise<DeviceUpdateAuditItem[]> => {
  try {
    if (!(organizations?.length || fleets?.length)) {
      throw new BadRequestError([
        {'field': 'organizations', 'message': 'At least one of organizations or fleets must be provided'},
        {'field': 'fleets', 'message': 'At least one of organizations or fleets must be provided'}
      ], 'At least one of organizations or fleets must be provided')
    }

    const res = await post<DeviceUpdatesRequest, unknown>({
      path: '/api/v1/devices/updatesAudit',
      body: {
        organizationIds: organizations,
        fleetIds: fleets
      }
    })

    return formatDeviceUpdateAuditResponse(res)
  } catch (e) {
    console.error('Error fetching device updates', e)
    throw e
  }
}

export const remediateDeviceSoftwareUpdate = async (
  deviceId: number
): Promise<DeviceSoftwareRemediationResult> => {
  try {
    const res = await post<Record<string, never>, unknown>({
      path: `/api/v1/devices/${deviceId}/remediateSoftwareUpdate`,
      body: {}
    })

    return formatDeviceSoftwareRemediationResponse(res)
  } catch (e) {
    console.error('Error remediating device software update', e)
    throw e
  }
}

export const remediateDeviceSoftwareUpdates = async (
  deviceIds: number[]
): Promise<DeviceSoftwareBulkRemediationResult> => {
  try {
    const res = await post<{ deviceIds: number[] }, unknown>({
      path: '/api/v1/devices/remediateSoftwareUpdates',
      body: { deviceIds }
    })

    return formatDeviceSoftwareBulkRemediationResponse(res)
  } catch (e) {
    console.error('Error remediating device software updates', e)
    throw e
  }
}
